/**
 * Seed a default, routable `TaxiPlan` from the synthesized `FieldGraph` (plus
 * any authored exit points) so the editor opens with the real network to
 * fine-tune rather than a blank field.
 *
 * Topology: the runway-parallel spine is chained through its gates
 * (spineA → g1 → … → gN → spineB) as `taxiway` edges, the two hold-short
 * runway-access nodes hang off the spine ends as `exit` edges, and each
 * exit-to-taxiway node connects to its nearest spine node. Everything lands in
 * one "Synthesized" group so it can be re-generated / cleared as a unit.
 */
import type { FieldGraph } from './fieldGraph'
import {
  alwaysActive,
  makeId,
  type TaxiEdge,
  type TaxiGroup,
  type TaxiNode,
  type TaxiPlan,
} from './taxiGraph'

/** An authored exit point, already converted to scene feet. */
export interface SeedExit {
  name: string
  /** scene feet [x, z] */
  pos: [number, number]
  headingDeg?: number | null
  throughputPerSlot?: number | null
}

const dist = (a: readonly [number, number], b: readonly [number, number]) =>
  Math.hypot(a[0] - b[0], a[1] - b[1])

function mkNode(
  groupId: string,
  pos: [number, number],
  role: TaxiNode['role'],
  extra?: Partial<TaxiNode>
): TaxiNode {
  return {
    id: makeId('n'),
    groupId,
    pos: [pos[0], pos[1]],
    role,
    headingDeg: extra?.headingDeg ?? null,
    throughputPerSlot: extra?.throughputPerSlot ?? null,
    active: alwaysActive(),
    flags: {},
  }
}

function mkEdge(groupId: string, a: string, b: string, role: TaxiEdge['role']): TaxiEdge {
  return {
    id: makeId('e'),
    groupId,
    a,
    b,
    role,
    directed: false,
    weightMult: 1,
    active: alwaysActive(),
    closures: [],
    flags: {},
  }
}

/**
 * An empty baseline plan with a single group and no nodes/edges — used when
 * there is no field graph to seed from (e.g. authoring from scratch in the
 * Layout Designer).
 */
export function buildEmptyPlan(locationId: string, name = 'Baseline'): TaxiPlan {
  const group: TaxiGroup = {
    id: makeId('g'),
    name: 'Default',
    role: 'permanent',
    color: '#34d399',
    active: alwaysActive(),
    flags: {},
  }
  return {
    id: makeId('plan'),
    locationId,
    name,
    description: 'Author taxi waypoints from scratch.',
    isBaseline: true,
    active: alwaysActive(),
    groups: [group],
    nodes: [],
    edges: [],
    runways: [],
    flags: {},
  }
}

/**
 * Build a baseline plan. `id`/`locationId` are placeholders until persisted —
 * the caller (or `upsertTaxiPlan`) assigns the real DB id.
 */
export function buildSeedPlan(
  locationId: string,
  field: FieldGraph,
  exits: SeedExit[] = [],
  name = 'Synthesized baseline'
): TaxiPlan {
  const group: TaxiGroup = {
    id: makeId('g'),
    name: 'Synthesized',
    role: 'permanent',
    color: '#34d399',
    active: alwaysActive(),
    flags: {},
  }

  const nodes: TaxiNode[] = []
  const edges: TaxiEdge[] = []

  // Spine endpoints.
  const spineA = mkNode(group.id, field.spine[0], 'taxi')
  const spineB = mkNode(group.id, field.spine[1], 'taxi')
  nodes.push(spineA, spineB)

  // Gates lie on the spine — chain them in order from spineA to spineB.
  const dir: [number, number] = [
    field.spine[1][0] - field.spine[0][0],
    field.spine[1][1] - field.spine[0][1],
  ]
  const along = (p: readonly [number, number]) =>
    (p[0] - field.spine[0][0]) * dir[0] + (p[1] - field.spine[0][1]) * dir[1]
  const gateNodes = field.gates
    .slice()
    .sort((g1, g2) => along(g1.center) - along(g2.center))
    .map((g) =>
      mkNode(group.id, g.center, 'gate', {
        headingDeg: g.headingDeg,
        throughputPerSlot: g.throughputPerSlot,
      })
    )
  nodes.push(...gateNodes)

  // Chain spineA → gates → spineB as the taxiway spine.
  const chain = [spineA, ...gateNodes, spineB]
  for (let i = 0; i < chain.length - 1; i++) {
    edges.push(mkEdge(group.id, chain[i].id, chain[i + 1].id, 'taxiway'))
  }

  // Hold-short runway-access nodes hang off the nearer spine end.
  for (const acc of field.runwayAccess) {
    const hs = mkNode(group.id, acc, 'hold_short')
    nodes.push(hs)
    const nearer = dist(acc, spineA.pos) <= dist(acc, spineB.pos) ? spineA : spineB
    edges.push(mkEdge(group.id, hs.id, nearer.id, 'exit'))
  }

  // Authored exit-to-taxiway points connect to their nearest existing node.
  const spineSide = [spineA, ...gateNodes, spineB]
  for (const ex of exits) {
    const en = mkNode(group.id, ex.pos, 'exit_to_taxiway', {
      headingDeg: ex.headingDeg ?? null,
      throughputPerSlot: ex.throughputPerSlot ?? null,
    })
    en.flags = { name: ex.name }
    nodes.push(en)
    let nearest = spineSide[0]
    let best = Infinity
    for (const cand of spineSide) {
      const d = dist(ex.pos, cand.pos)
      if (d < best) {
        best = d
        nearest = cand
      }
    }
    edges.push(mkEdge(group.id, en.id, nearest.id, 'exit'))
  }

  return {
    id: makeId('plan'),
    locationId,
    name,
    description: 'Auto-generated from the synthesized field graph. Fine-tune as needed.',
    isBaseline: true,
    active: alwaysActive(),
    groups: [group],
    nodes,
    edges,
    runways: [],
    flags: { seeded: true },
  }
}
